import { useState, useRef } from "react";
import { api, API, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Upload, X, Image as ImageIcon } from "lucide-react";

/**
 * LogoUpload: input file → upload → returns upload_id via onChange.
 * `value` = upload_id (string) or null. Preview via /api/uploads/{id}.
 */
export default function LogoUpload({ value, onChange, scope = "team", size = "md", testid = "logo-upload" }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const dims = size === "sm" ? "w-14 h-14" : size === "lg" ? "w-24 h-24" : "w-20 h-20";

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 3MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/uploads/logo?scope=${scope}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.(r.data.upload_id);
      toast.success("🖼️ Logo caricato!");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const previewSrc = value?.startsWith("http")
    ? value
    : value ? `${API}/uploads/${value}` : null;

  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <div className={`${dims} rounded-lg border-2 border-dashed border-slate-700 bg-slate-900 overflow-hidden flex items-center justify-center relative group`}>
        {previewSrc ? (
          <>
            <img src={previewSrc} alt="logo" className="w-full h-full object-cover"/>
            <button type="button" onClick={()=>onChange?.(null)}
              className="absolute top-1 right-1 w-5 h-5 rounded-full bg-rose-500 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
              data-testid={`${testid}-clear`} title="Rimuovi">
              <X size={12}/>
            </button>
          </>
        ) : (
          <ImageIcon size={20} className="text-slate-600"/>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <button type="button" onClick={()=>inputRef.current?.click()} disabled={uploading}
          data-testid={`${testid}-btn`}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-700 hover:border-emerald-500 hover:text-emerald-300 text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-50">
          <Upload size={12}/> {uploading ? "Carico…" : (previewSrc ? "Cambia" : "Carica logo")}
        </button>
        <span className="text-[10px] text-slate-500">PNG · JPG · WEBP (max 3MB)</span>
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
          className="hidden" onChange={e=>upload(e.target.files?.[0])} data-testid={`${testid}-file`}/>
      </div>
    </div>
  );
}
